<!-- Step selector -->
<div class="flex flex-wrap justify-center gap-2 sm:gap-4 mb-10">
  @for (step of steps; track step.tab; let i = $index) {
    <button
      type="button"
      (click)="selectStep(i)"
      [attr.aria-current]="activeStep() === i ? 'step' : null"
      [class]="
        activeStep() === i
          ? 'bg-purple-600 text-white shadow-lg shadow-purple-300/40'
          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
      "
      class="px-4 sm:px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300"
    >
      {{ step.tab }}
    </button>
  }
</div>

<div class="grid md:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
  <!-- Left: mockup screen -->
  <div class="order-2 md:order-1">
    <div
      class="bg-gray-50 dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-xl"
    >
      <!-- Browser bar -->
      <div
        class="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700"
        aria-hidden="true"
      >
        <div class="flex gap-1.5">
          <div class="w-3 h-3 rounded-full bg-red-400"></div>
          <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
          <div class="w-3 h-3 rounded-full bg-green-400"></div>
        </div>
        <div
          class="flex-1 text-center text-xs text-gray-400 dark:text-gray-500 font-mono"
        >
          predivent.com/dashboard
        </div>
      </div>

      <!-- Screen content -->
      <div class="p-6 min-h-[320px]">
        @switch (activeStep()) {
          @case (0) {
            <!-- Scene 1: the daily question -->
            <div class="space-y-5 animate-fadeIn">
              <p
                class="text-center text-[11px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400"
              >
                Boulangeries · Food trucks · Commerces
              </p>
              <p
                class="text-center text-lg font-bold leading-snug text-gray-900 dark:text-white"
              >
                Chaque jour, la même question :<br />
                <span class="gradient-text text-2xl font-extrabold"
                  >combien en préparer ?</span
                >
              </p>
              <div class="grid grid-cols-2 gap-3">
                @for (card of problemCards; track card.keyword) {
                  <div
                    class="relative rounded-xl border p-4 text-center"
                    [class]="card.cardClass"
                  >
                    @if (card.tag) {
                      <span
                        class="absolute -top-2 right-2 rotate-6 rounded bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase text-white shadow"
                        >{{ card.tag }}</span
                      >
                    }
                    <div class="text-3xl mb-2" aria-hidden="true">
                      {{ card.icon }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ card.question }}
                    </div>
                    <div class="text-lg font-extrabold" [class]="card.keywordClass">
                      {{ card.keyword }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ card.detail }}
                    </div>
                  </div>
                }
              </div>
            </div>
          }
          @case (1) {
            <!-- Scene 2: AI analysis -->
            <div class="space-y-4 animate-fadeIn">
              <p
                class="text-center text-lg font-bold text-gray-900 dark:text-white"
              >
                Votre expertise métier,
                <span class="gradient-text">augmentée par l'IA</span>
              </p>
              <div class="grid grid-cols-2 gap-2">
                @for (source of dataSources; track source.title) {
                  <div
                    class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <span class="text-lg" aria-hidden="true">{{
                      source.icon
                    }}</span>
                    <div class="min-w-0">
                      <div
                        class="text-xs font-semibold text-gray-800 dark:text-gray-200"
                      >
                        {{ source.title }}
                      </div>
                      <div class="text-[11px] text-gray-400">
                        {{ source.detail }}
                      </div>
                    </div>
                  </div>
                }
              </div>
              <div class="flex items-center justify-center gap-3">
                <span
                  class="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 text-lg font-extrabold text-white shadow-lg shadow-purple-300/40"
                  >IA</span
                >
                <span class="text-gray-400" aria-hidden="true">→</span>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  >✓ Prévision prête</span
                >
              </div>
            </div>
          }
          @case (2) {
            <!-- Scene 3: recommended production -->
            <div class="space-y-3 animate-fadeIn">
              <div>
                <p
                  class="text-[11px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400"
                >
                  Prévision · Samedi
                </p>
                <p class="font-bold text-gray-900 dark:text-white">
                  Production recommandée
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                @for (item of forecastContext; track item) {
                  <span
                    class="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-300"
                    >{{ item }}</span
                  >
                }
              </div>
              <div class="space-y-2">
                @for (row of forecastRows; track row.product) {
                  <div
                    class="rounded-lg border border-gray-200 bg-white p-2.5 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <div class="flex items-center justify-between gap-2">
                      <div class="min-w-0">
                        <div
                          class="text-sm font-semibold text-gray-800 dark:text-gray-200"
                        >
                          {{ row.icon }} {{ row.product }}
                        </div>
                        <div class="text-[11px] text-gray-400">
                          samedi dernier : {{ row.lastWeek }}
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <span
                          class="text-lg font-bold text-purple-700 dark:text-purple-300"
                          >{{ row.quantity }}</span
                        >
                        <span
                          class="rounded-full px-2 py-0.5 text-xs font-semibold"
                          [class]="row.trendClass"
                          >{{ row.trendLabel }}</span
                        >
                      </div>
                    </div>
                    <div
                      class="mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-slate-700"
                      aria-hidden="true"
                    >
                      <div
                        class="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500"
                        [style.width.%]="row.barPercent"
                      ></div>
                    </div>
                  </div>
                }
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                🕐 Mis à jour à 5&nbsp;h&nbsp;00, avant la première fournée
              </p>
              <ul
                class="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300"
              >
                @for (benefit of benefits; track benefit) {
                  <li>✓ {{ benefit }}</li>
                }
              </ul>
            </div>
          }
        }
      </div>
    </div>
  </div>

  <!-- Right: steps -->
  <div class="order-1 md:order-2 space-y-6">
    @for (step of steps; track step.tab; let i = $index) {
      <button
        type="button"
        (click)="selectStep(i)"
        [attr.aria-current]="activeStep() === i ? 'step' : null"
        [class]="
          activeStep() === i
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10'
            : 'border-gray-200 dark:border-slate-700 hover:border-purple-300'
        "
        class="block w-full text-left border-2 rounded-xl p-5 transition-all duration-300"
      >
        <span class="flex items-center gap-3 mb-2">
          <span
            [class]="
              activeStep() === i
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
            "
            class="w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm transition-colors"
          >
            {{ i + 1 }}
          </span>
          <span class="font-bold text-gray-900 dark:text-white">
            {{ step.title }}
          </span>
        </span>
        @if (activeStep() === i) {
          <span
            class="block text-sm text-gray-600 dark:text-gray-400 leading-relaxed ml-11 animate-fadeIn"
          >
            {{ step.description }}
          </span>
        }
      </button>
    }

    <p class="ml-11 text-lg font-extrabold text-gray-900 dark:text-white">
      Prévoyez juste. Gaspillez moins.
      <span class="gradient-text">Vendez plus.</span>
    </p>
  </div>
</div>
